/**
 * Service for managing user statistics with Firebase Realtime Database
 * Only premium users are saved to the database
 */

import { database } from '../firebase/config.js';
import { ref, get } from 'firebase/database';

/**
 * Cache for storing user count to avoid excessive database calls
 */
let cachedUserCount = null;
let lastUserFetchTime = null;
const USER_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get premium user count from Firebase Realtime Database
 * @param {boolean} forceRefresh - Force refresh of cached data
 * @returns {Promise<number>} Number of premium users
 */
export async function getPremiumUserCount(forceRefresh = false) {
  // Return cached data if available and not expired
  if (!forceRefresh && cachedUserCount !== null && lastUserFetchTime &&
      (Date.now() - lastUserFetchTime) < USER_CACHE_DURATION) {
    return cachedUserCount;
  }

  try {
    const userCountRef = ref(database, 'stats/userCount');
    const snapshot = await get(userCountRef);

    if (snapshot.exists()) {
      const userCount = snapshot.val();

      // Cache the result
      cachedUserCount = userCount;
      lastUserFetchTime = Date.now();

      return userCount;
    } else {
      // No count found, return 0
      cachedUserCount = 0;
      lastUserFetchTime = Date.now();
      return 0;
    }
  } catch (error) {
    console.error('Error fetching premium user count:', error);
    // Return cached value if available, otherwise 0
    return cachedUserCount !== null ? cachedUserCount : 0;
  }
}

/**
 * Get total generated project count from Firebase Realtime Database
 * @returns {Promise<number>} Number of generated projects
 */
export async function getGeneratedProjectCount() {
  try {
    const projectCountRef = ref(database, 'stats/generatedProjectCount');
    const snapshot = await get(projectCountRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return 0;
    }
  } catch (error) {
    console.error('Error fetching generated project count:', error);
    return 0;
  }
}

/**
 * Get premium generated project count from Firebase Realtime Database
 * @returns {Promise<number>} Number of premium generated projects
 */
export async function getGeneratedPremiumProjectCount() {
  try {
    const premiumProjectCountRef = ref(database, 'stats/generatedPremiumProjectCount');
    const snapshot = await get(premiumProjectCountRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return 0;
    }
  } catch (error) {
    console.error('Error fetching generated premium project count:', error);
    return 0;
  }
}

/**
 * Clear the user count cache to force refresh on next call
 */
export function clearUserCountCache() {
  cachedUserCount = null;
  lastUserFetchTime = null;
}

const userService = {
  getPremiumUserCount,
  getGeneratedProjectCount,
  getGeneratedPremiumProjectCount,
  clearUserCountCache
};

export default userService;